import axios, { type AxiosResponse } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const ROOT_API_URL = API_URL.replace(/\/api\/?$/, '') || '';
const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'local';
const API_TOKEN_STORAGE_KEY = 'perfectodds_access_token';
const CURRENT_USER_STORAGE_KEY = 'perfectodds_current_user';

const createApiClient = (baseURL: string) => {
  const client = axios.create({
    baseURL,
  });

  client.interceptors.request.use((config) => {
    const token = AUTH_MODE === 'api' ? localStorage.getItem(API_TOKEN_STORAGE_KEY) : null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (AUTH_MODE === 'api' && error?.response?.status === 401) {
        localStorage.removeItem(API_TOKEN_STORAGE_KEY);
        localStorage.removeItem(CURRENT_USER_STORAGE_KEY);
      }
      return Promise.reject(error);
    },
  );

  return client;
};

const api = createApiClient(API_URL);
const rootApi = ROOT_API_URL === API_URL ? api : createApiClient(ROOT_API_URL);
const hasRootFallback = ROOT_API_URL !== API_URL;

const requestWithRootFallback = async <T>(
  request: () => Promise<AxiosResponse<T>>,
  fallbackRequest: () => Promise<AxiosResponse<T>>,
) => {
  try {
    const response = await request();
    const contentType = String(response.headers?.['content-type'] || response.headers?.['Content-Type'] || '').toLowerCase();
    const data = response.data;
    const looksLikeHtml = typeof data === 'string' && /<!doctype html>|<html[\s>]/i.test(data);
    if (hasRootFallback && (contentType.includes('text/html') || looksLikeHtml)) {
      return await fallbackRequest();
    }
    return response;
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (!hasRootFallback || status === undefined || (status !== 404 && status !== 405)) {
      throw error;
    }
    return fallbackRequest();
  }
};

export const authAPI = {
  register: (email: string, password: string, referralCode?: string) =>
    requestWithRootFallback(
      () => api.post('/auth/register', { email, password, referralCode }),
      () => rootApi.post('/auth/register', { email, password, referralCode }),
    ),
  getGoogleConfig: () => requestWithRootFallback(
    () => api.get('/auth/google/config'),
    () => rootApi.get('/auth/google/config'),
  ),
  googleAuth: (credential: string, acceptedTerms = true, referralCode?: string) => requestWithRootFallback(
    () => api.post('/auth/google', { credential, acceptedTerms, referralCode }),
    () => rootApi.post('/auth/google', { credential, acceptedTerms, referralCode }),
  ),
  googleLogin: (credential: string) => requestWithRootFallback(
    () => api.post('/auth/google/login', { credential }),
    () => rootApi.post('/auth/google/login', { credential }),
  ),
  login: (email: string, password: string) =>
    requestWithRootFallback(
      () => api.post('/auth/login', { email, password }),
      () => rootApi.post('/auth/login', { email, password }),
    ),
  getMe: () => requestWithRootFallback(
    () => api.get('/auth/me'),
    () => rootApi.get('/auth/me'),
  ),
  patchMe: (payload: { name?: string; email?: string; telegramName?: string; timezone?: string; language?: string }) =>
    requestWithRootFallback(
      () => api.post('/auth/me/update', payload),
      () => rootApi.post('/auth/me/update', payload),
    ),
  changePassword: (payload: { currentPassword: string; newPassword: string }) => requestWithRootFallback(
    () => api.post('/auth/change-password', payload),
    () => rootApi.post('/auth/change-password', payload),
  ),
  deleteAccount: (payload: { password: string }) => requestWithRootFallback(
    () => api.post('/auth/delete-account', payload),
    () => rootApi.post('/auth/delete-account', payload),
  ),
  getTelegramLink: () => requestWithRootFallback(
    () => api.get('/auth/telegram-link'),
    () => rootApi.get('/auth/telegram-link'),
  ),
  startTelegramLink: () => requestWithRootFallback(
    () => api.post('/auth/telegram-link/start'),
    () => rootApi.post('/auth/telegram-link/start'),
  ),
  confirmTelegramLink: (payload: { chatId: string; username?: string }) =>
    requestWithRootFallback(
      () => api.post('/auth/telegram-link/confirm', payload),
      () => rootApi.post('/auth/telegram-link/confirm', payload),
    ),
  unlinkTelegram: () => requestWithRootFallback(
    () => api.post('/auth/telegram-link/unlink'),
    () => rootApi.post('/auth/telegram-link/unlink'),
  ),
  syncTelegramAccess: () => requestWithRootFallback(
    () => api.post('/auth/telegram-access/sync'),
    () => rootApi.post('/auth/telegram-access/sync'),
  ),
  logout: () => requestWithRootFallback(
    () => api.post('/auth/logout'),
    () => rootApi.post('/auth/logout'),
  ),
  forgotPassword: (email: string) => requestWithRootFallback(
    () => api.post('/auth/forgot-password', { email }),
    () => rootApi.post('/auth/forgot-password', { email }),
  ),
  resetPassword: (payload: { email: string; code: string; password: string }) => requestWithRootFallback(
    () => api.post('/auth/reset-password', payload),
    () => rootApi.post('/auth/reset-password', payload),
  ),
};

export interface ReferralOverviewApi {
  code: string;
  shareUrl: string;
  rewardAmount: number;
  availableRewards: number;
  reservedRewards: number;
  redeemedRewards: number;
  qualifiedFriends: number;
  invitedFriends: number;
  friends: Array<{
    id: number;
    label: string;
    status: 'pending' | 'qualified';
    createdAt: string;
    qualifiedAt?: string | null;
  }>;
}

export const referralAPI = {
  validate: (code: string) => requestWithRootFallback(
    () => api.get(`/referrals/validate/${encodeURIComponent(code)}`),
    () => rootApi.get(`/referrals/validate/${encodeURIComponent(code)}`),
  ),
  getMine: () => requestWithRootFallback(
    () => api.get('/referrals/me'),
    () => rootApi.get('/referrals/me'),
  ),
};

export const billingAPI = {
  createCheckoutSession: (plan: 'pro' | 'pre-match' | 'live', billingCycle: 'month' | 'week' | 'day-pass') =>
    requestWithRootFallback(
      () => api.post('/billing/checkout-session', { plan, billingCycle }),
      () => rootApi.post('/billing/checkout-session', { plan, billingCycle }),
    ),
  activateFreePlan: () =>
    requestWithRootFallback(
      () => api.post('/billing/activate-free-plan'),
      () => rootApi.post('/billing/activate-free-plan'),
    ),
  getCheckoutSession: (sessionId: string) =>
    requestWithRootFallback(
      () => api.get(`/billing/checkout-session/${encodeURIComponent(sessionId)}`),
      () => rootApi.get(`/billing/checkout-session/${encodeURIComponent(sessionId)}`),
    ),
  listInvoices: () => requestWithRootFallback(
    () => api.get('/billing/invoices'),
    () => rootApi.get('/billing/invoices'),
  ),
  getSubscriptionSummary: () => requestWithRootFallback<{ subscription: BillingSubscriptionSummaryApi }>(
    () => api.get('/billing/subscription-summary'),
    () => rootApi.get('/billing/subscription-summary'),
  ),
  createPortalSession: (flow: 'manage' | 'subscription_update' | 'subscription_cancel' = 'manage') => requestWithRootFallback(
    () => api.post('/billing/portal-session', { flow }),
    () => rootApi.post('/billing/portal-session', { flow }),
  ),
};

export interface BillingSubscriptionSummaryApi {
  managedByStripe: boolean;
  recurring: boolean;
  status: string;
  cancelAtPeriodEnd: boolean;
  cancellationDate?: string | null;
  nextCharge?: {
    date?: string | null;
    amount: number;
    subtotal: number;
    discountAmount: number;
    taxAmount: number;
    currency: string;
    plan: 'pro' | 'pre-match' | 'live';
    billingCycle: 'month' | 'week';
    estimated: boolean;
    discounts: Array<{
      name: string;
      amount: number;
      duration?: string | null;
      isReferral: boolean;
    }>;
  } | null;
  paymentMethod?: {
    type: string;
    billingName?: string | null;
    brand?: string | null;
    last4?: string | null;
    expiryMonth?: number | null;
    expiryYear?: number | null;
    funding?: string | null;
    bankCode?: string | null;
    country?: string | null;
    bank?: string | null;
  } | null;
  scheduledChange?: {
    plan: 'pro' | 'pre-match' | 'live';
    billingCycle: 'month' | 'week';
    effectiveAt?: string | null;
  } | null;
}

export interface BillingInvoiceApiItem {
  id: string;
  number?: string | null;
  status: string;
  currency: string;
  amountDue: number;
  amountPaid: number;
  subtotal: number;
  discountAmount: number;
  createdAt: string;
  paidAt?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  hostedInvoiceUrl?: string | null;
  invoicePdf?: string | null;
}

export interface TrackedSurebetApiInput {
  surebetHistoryId?: string | null;
  placedAt?: string | null;
  eventName: string;
  sport: string;
  competition?: string | null;
  market: string;
  selection?: string | null;
  line?: number | null;
  type: string;
  status: 'Open' | 'Afgerond' | 'Void' | 'Deels void' | 'Niet geplaatst' | 'Fout geplaatst';
  result: 'Nog niet bekend' | 'A gewonnen' | 'B gewonnen' | 'C gewonnen' | 'Void' | 'Deels void' | 'Niet geplaatst' | 'Fout geplaatst';
  notes?: string | null;
  bookmakerA: string;
  directionA: string;
  originalOddA: number;
  placedOddA: number;
  stakeA: number;
  bookmakerB: string;
  directionB: string;
  originalOddB: number;
  placedOddB: number;
  stakeB: number;
  bookmakerC?: string | null;
  directionC?: string | null;
  originalOddC?: number | null;
  placedOddC?: number | null;
  stakeC?: number | null;
}

export interface TrackedSurebetApiUpdate {
  placedAt?: string | null;
  placedOddA?: number;
  placedOddB?: number;
  placedOddC?: number | null;
  stakeA?: number;
  stakeB?: number;
  stakeC?: number | null;
  status?: 'Open' | 'Afgerond' | 'Void' | 'Deels void' | 'Niet geplaatst' | 'Fout geplaatst';
  result?: 'Nog niet bekend' | 'A gewonnen' | 'B gewonnen' | 'C gewonnen' | 'Void' | 'Deels void' | 'Niet geplaatst' | 'Fout geplaatst';
  notes?: string | null;
}

export interface TrackerCostApiInput {
  date: string;
  type: 'Abonnement' | 'Kosten' | 'Correctie';
  description: string;
  amount: number;
}

export interface TrackerCostApiUpdate {
  date?: string;
  type?: 'Abonnement' | 'Kosten' | 'Correctie';
  description?: string;
  amount?: number;
}

export interface SurebetHistoryApiItem {
  id: string;
  createdAt: string;
  date: string;
  eventName: string;
  sport: string;
  competition?: string | null;
  market: string;
  selection?: string | null;
  line?: number | null;
  type: string;
  bookmakerA: string;
  directionA: string;
  oddA: number;
  suggestedStakeA: number;
  bookmakerB: string;
  directionB: string;
  oddB: number;
  suggestedStakeB: number;
  bookmakerC?: string | null;
  directionC?: string | null;
  oddC?: number | null;
  suggestedStakeC?: number | null;
  totalStake: number;
  minimumProfit: number;
  roiPercent: number;
}

export interface AdminAppControlResponse {
  appEnabled: boolean;
  effectiveEnabled: boolean;
  providerConfigError?: string | null;
  schedulerConfigured: boolean;
  liveWsConfigured: boolean;
  schedulerRunning: boolean;
  liveWsRunning: boolean;
  prematchEnabled: boolean;
  liveEnabled: boolean;
  telegramSendEnabled: boolean;
  prematchTelegramSendEnabled: boolean;
  liveTelegramSendEnabled: boolean;
  minRoiPercent: number;
  maxSendsPerTick: number;
  totalStake: number;
  environment: string;
}

export interface SchedulerRunSummary {
  mode: string;
  candidatesFound: number;
  created: number;
  updated: number;
  eligibleMessages: number;
  skipped: number;
  sent: number;
  errors: number;
  telegramSendEnabled: boolean;
  sentItems: unknown[];
  skippedBecausePreviousRunStillActive?: boolean;
}

export interface SchedulerStatusResponse {
  schedulerEnabled: boolean;
  telegramSendEnabled: boolean;
  prematchTelegramSendEnabled: boolean;
  liveOnlyMode: boolean;
  mode: string;
  pollIntervalSeconds: number;
  maxSendsPerTick: number;
  running: boolean;
  lastRunAt?: string | null;
  lastRunStartedAt?: string | null;
  lastRunFinishedAt?: string | null;
  lastRunDurationSeconds?: number | null;
  averageRunDurationSeconds?: number | null;
  completedRuns: number;
  skippedOverlappingRuns: number;
  lastSkippedAt?: string | null;
  lastSkipReason?: string | null;
  lastRunSummary: SchedulerRunSummary;
}

export interface LiveWsProcessSummary {
  mode: string;
  telegramSendEnabled: boolean;
  candidatesFound: number;
  pairCount: number;
  created: number;
  updated: number;
  sent: number;
  errors: number;
  sentItems: unknown[];
}

export interface LiveWsConnectionStatus {
  connected: boolean;
  framesReceived: number;
  eventCount: number;
  lastFrameAt?: string | null;
  lastTimestamp?: string | null;
  connectionUrlSafe?: string | null;
  closeCode?: number | null;
  closeReason?: string | null;
  errors: string[];
}

export interface LiveWsStatusResponse {
  workerEnabled: boolean;
  running: boolean;
  telegramSendEnabled: boolean;
  liveTelegramSendEnabled: boolean;
  mode: string;
  sport: string;
  bookmakers: string[];
  processIntervalSeconds: number;
  listenerDurationSeconds: number;
  maxSizeBytes: number;
  lastProcessAt?: string | null;
  lastProcessStartedAt?: string | null;
  lastProcessFinishedAt?: string | null;
  lastProcessDurationSeconds?: number | null;
  averageProcessDurationSeconds?: number | null;
  completedProcessRuns: number;
  lastProcessSummary: LiveWsProcessSummary;
  connections: Record<string, LiveWsConnectionStatus>;
}

export interface StoredSurebetBookmaker {
  key: string;
  name: string;
  platform: string;
  direction: string;
  outcomeName: string;
  odd: number;
}

export interface StoredSurebetStakeExample {
  totalStake: number;
  stakeA: number;
  stakeB: number;
  returnA: number;
  returnB: number;
  minProfit: number;
}

export interface StoredSurebetCandidateApiItem {
  id: number;
  candidateKey: string;
  source: string;
  status: string;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
  sentAt?: string | null;
  telegramMessageId?: number | null;
  eventId?: string | null;
  eventName: string;
  competition?: string | null;
  startTime?: string | null;
  normalizedMarket: string;
  marketName: string;
  line?: string | null;
  selection?: string | null;
  candidateType: string;
  bookmakerA: StoredSurebetBookmaker;
  bookmakerB: StoredSurebetBookmaker;
  impliedProbability: number;
  roiPercent: number;
  confidence: number;
  qualityFlags: string[];
  warnings: string[];
  outcomes: Array<Record<string, unknown>>;
  stakeExample: StoredSurebetStakeExample;
  telegram: {
    message: string;
    parseMode: string;
    disableWebPagePreview: boolean;
  };
}

export interface StoredSurebetsResponse {
  count: number;
  summary: {
    totalStored: number;
    found: number;
    updated: number;
    sent: number;
    expired: number;
    ignored: number;
  };
  candidates: StoredSurebetCandidateApiItem[];
}

export interface TelegramTestSendResponse {
  status: string;
  channel: 'prematch' | 'live';
  message_id: number | string | null;
  match: string;
  market: string;
  roi_percent: number;
}

export interface AdminUserApiItem {
  id: number;
  email: string;
  name: string;
  role: 'user' | 'admin' | string;
  plan: 'none' | 'pro' | 'pre-match' | 'live' | string;
  subscriptionStatus: 'none' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'expired' | string;
  billingCycle?: 'none' | 'month' | 'week' | 'day-pass' | string;
  billingManagedByStripe: boolean;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  subscriptionEndsAt?: string | null;
  telegramUsername?: string | null;
  telegramChatId?: string | null;
  telegramLinked: boolean;
  telegramLinkedAt?: string | null;
  telegramAccessStatus: string;
  telegramAccessSyncedAt?: string | null;
  telegramAccessError?: string | null;
  timezone?: string | null;
  language?: string | null;
  trackedSurebetCount?: number;
  lastTrackerActivityAt?: string | null;
  stripePaymentCount?: number;
  stripePaidTotal?: number;
  lastStripePaymentAt?: string | null;
  referralInvites?: number;
  qualifiedReferrals?: number;
  availableReferralRewards?: number;
  redeemedReferralRewards?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastLoginAt?: string | null;
}

export interface AdminUsersResponse {
  users: AdminUserApiItem[];
  count: number;
}

export interface AdminUserUpdatePayload {
  role?: 'user' | 'admin';
  plan?: 'none' | 'pro' | 'pre-match' | 'live';
  subscriptionStatus?: 'none' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'expired';
  subscriptionEndsAt?: string | null;
}

export interface AdminUserUpdateResponse {
  user: AdminUserApiItem;
  telegramSync?: { status?: string; error?: string } | null;
}

export interface AdminUserTelegramInvitesResponse {
  ok: boolean;
  message: string;
  linksSent: number;
  channels: string[];
  telegramAccess?: { status?: string; inviteLinks?: Array<{ channel: string; inviteLink: string }> } | null;
  user: AdminUserApiItem;
}

export interface SupportContactPayload {
  name: string;
  email: string;
  topic: string;
  plan?: string | null;
  message: string;
}

export interface SupportRequestApiItem {
  id: number;
  name: string;
  email: string;
  topic: string;
  topicLabel: string;
  plan?: string | null;
  planLabel: string;
  message: string;
  status: 'new' | 'in_progress' | 'closed' | string;
  mailDeliveryStatus: string;
  mailDeliveryError?: string | null;
  mailSentAt?: string | null;
  handledAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SupportContactResponse {
  request: SupportRequestApiItem;
  mailSent: boolean;
}

export interface AdminSupportRequestsResponse {
  requests: SupportRequestApiItem[];
  count: number;
}

export interface AdminSupportRequestUpdateResponse {
  request: SupportRequestApiItem;
}

export const trackerApi = {
  getTrackerHistory: (params?: { limit?: number; minRoi?: number; startDate?: string; endDate?: string; includeCurrentDay?: boolean }) => requestWithRootFallback(
    () => api.get<SurebetHistoryApiItem[]>('/tracker/history', {
      params: {
        limit: params?.limit,
        min_roi: params?.minRoi,
        start_date: params?.startDate,
        end_date: params?.endDate,
        include_current_day: params?.includeCurrentDay,
      },
    }),
    () => rootApi.get<SurebetHistoryApiItem[]>('/tracker/history', {
      params: {
        limit: params?.limit,
        min_roi: params?.minRoi,
        start_date: params?.startDate,
        end_date: params?.endDate,
        include_current_day: params?.includeCurrentDay,
      },
    }),
  ),
  getTrackedSurebets: () => requestWithRootFallback(
    () => api.get('/tracker/surebets'),
    () => rootApi.get('/tracker/surebets'),
  ),
  createTrackedSurebet: (payload: TrackedSurebetApiInput) => requestWithRootFallback(
    () => api.post('/tracker/surebets', payload),
    () => rootApi.post('/tracker/surebets', payload),
  ),
  updateTrackedSurebet: (id: number | string, payload: TrackedSurebetApiUpdate) => requestWithRootFallback(
    () => api.post(`/tracker/surebets/${id}/update`, payload),
    () => rootApi.post(`/tracker/surebets/${id}/update`, payload),
  ),
  deleteTrackedSurebet: (id: number | string) => requestWithRootFallback(
    () => api.post(`/tracker/surebets/${id}/delete`),
    () => rootApi.post(`/tracker/surebets/${id}/delete`),
  ),
  getTrackerCosts: () => requestWithRootFallback(
    () => api.get('/tracker/costs'),
    () => rootApi.get('/tracker/costs'),
  ),
  createTrackerCost: (payload: TrackerCostApiInput) => requestWithRootFallback(
    () => api.post('/tracker/costs', payload),
    () => rootApi.post('/tracker/costs', payload),
  ),
  updateTrackerCost: (id: number | string, payload: TrackerCostApiUpdate) => requestWithRootFallback(
    () => api.post(`/tracker/costs/${id}/update`, payload),
    () => rootApi.post(`/tracker/costs/${id}/update`, payload),
  ),
  deleteTrackerCost: (id: number | string) => requestWithRootFallback(
    () => api.post(`/tracker/costs/${id}/delete`),
    () => rootApi.post(`/tracker/costs/${id}/delete`),
  ),
};

export const supportApi = {
  submitContactRequest: (payload: SupportContactPayload) => requestWithRootFallback(
    () => api.post<SupportContactResponse>('/support/contact', payload),
    () => rootApi.post<SupportContactResponse>('/support/contact', payload),
  ),
};

export const adminApi = {
  getAppControl: () => requestWithRootFallback(
    () => api.get<AdminAppControlResponse>('/admin/app-control'),
    () => rootApi.get<AdminAppControlResponse>('/admin/app-control'),
  ),
  updateAppControl: (payload: {
    enabled?: boolean;
    prematchEnabled?: boolean;
    liveEnabled?: boolean;
    telegramSendEnabled?: boolean;
    prematchTelegramSendEnabled?: boolean;
    liveTelegramSendEnabled?: boolean;
    minRoiPercent?: number;
  }) => requestWithRootFallback(
    () => api.post<AdminAppControlResponse>('/admin/app-control', {
      enabled: payload.enabled,
      telegram_send_enabled: payload.telegramSendEnabled,
      prematch_enabled: payload.prematchEnabled,
      live_enabled: payload.liveEnabled,
      prematch_telegram_send_enabled: payload.prematchTelegramSendEnabled,
      live_telegram_send_enabled: payload.liveTelegramSendEnabled,
      min_roi_percent: payload.minRoiPercent,
    }),
    () => rootApi.post<AdminAppControlResponse>('/admin/app-control', {
      enabled: payload.enabled,
      telegram_send_enabled: payload.telegramSendEnabled,
      prematch_enabled: payload.prematchEnabled,
      live_enabled: payload.liveEnabled,
      prematch_telegram_send_enabled: payload.prematchTelegramSendEnabled,
      live_telegram_send_enabled: payload.liveTelegramSendEnabled,
      min_roi_percent: payload.minRoiPercent,
    }),
  ),
  getSchedulerStatus: () => requestWithRootFallback(
    () => api.get<SchedulerStatusResponse>('/ops/scheduler/status'),
    () => rootApi.get<SchedulerStatusResponse>('/ops/scheduler/status'),
  ),
  getLiveWsStatus: () => requestWithRootFallback(
    () => api.get<LiveWsStatusResponse>('/ops/live-ws/status'),
    () => rootApi.get<LiveWsStatusResponse>('/ops/live-ws/status'),
  ),
  getUsers: () => requestWithRootFallback(
    () => api.get<AdminUsersResponse>('/admin/users'),
    () => rootApi.get<AdminUsersResponse>('/admin/users'),
  ),
  getSupportRequests: () => requestWithRootFallback(
    () => api.get<AdminSupportRequestsResponse>('/admin/support-requests'),
    () => rootApi.get<AdminSupportRequestsResponse>('/admin/support-requests'),
  ),
  updateUser: (id: number, payload: AdminUserUpdatePayload) => requestWithRootFallback(
    () => api.post<AdminUserUpdateResponse>(`/admin/users/${id}`, {
      role: payload.role,
      plan: payload.plan,
      subscription_status: payload.subscriptionStatus,
      subscription_ends_at: payload.subscriptionEndsAt,
    }),
    () => rootApi.post<AdminUserUpdateResponse>(`/admin/users/${id}`, {
      role: payload.role,
      plan: payload.plan,
      subscription_status: payload.subscriptionStatus,
      subscription_ends_at: payload.subscriptionEndsAt,
    }),
  ),
  sendUserTelegramInvites: (id: number) => requestWithRootFallback(
    () => api.post<AdminUserTelegramInvitesResponse>(`/admin/users/${id}/telegram-invites/send`),
    () => rootApi.post<AdminUserTelegramInvitesResponse>(`/admin/users/${id}/telegram-invites/send`),
  ),
  updateSupportRequest: (id: number, payload: { status?: 'new' | 'in_progress' | 'closed' | string }) => requestWithRootFallback(
    () => api.post<AdminSupportRequestUpdateResponse>(`/admin/support-requests/${id}`, {
      status: payload.status,
    }),
    () => rootApi.post<AdminSupportRequestUpdateResponse>(`/admin/support-requests/${id}`, {
      status: payload.status,
    }),
  ),
  deleteUser: (id: number) => requestWithRootFallback(
    () => api.post(`/admin/users/${id}/delete`),
    () => rootApi.post(`/admin/users/${id}/delete`),
  ),
  getStoredSurebets: (params?: { limit?: number; search?: string; min_roi?: number; include_sent?: boolean }) => requestWithRootFallback(
    () => api.get<StoredSurebetsResponse>('/admin/stored-surebets', { params }),
    () => rootApi.get<StoredSurebetsResponse>('/admin/stored-surebets', { params }),
  ),
  deleteStoredSurebets: (ids: number[]) => requestWithRootFallback(
    () => api.post<{ deleted: number; requested: number }>('/admin/stored-surebets/delete', { ids }),
    () => rootApi.post<{ deleted: number; requested: number }>('/admin/stored-surebets/delete', { ids }),
  ),
  deleteTrackerHistory: (ids: number[]) => requestWithRootFallback(
    () => api.post<{ deleted: number; requested: number }>('/admin/tracker-history/delete', { ids }),
    () => rootApi.post<{ deleted: number; requested: number }>('/admin/tracker-history/delete', { ids }),
  ),
  sendTelegramTest: (channel: 'prematch' | 'live') => requestWithRootFallback(
    () => api.post<TelegramTestSendResponse>(`/admin/telegram/test-random/${channel}`),
    () => rootApi.post<TelegramTestSendResponse>(`/admin/telegram/test-random/${channel}`),
  ),
};

export default api;
